import type { CSSProperties } from "react"

import { cn } from "@/lib/utils"

interface WordmarkProps {
  variant?: "wordmark" | "mark"
  className?: string
  size?: number
  decorative?: boolean
}

type WordmarkTextStyle = CSSProperties & {
  "--wordmark-font-size": string
  "--wordmark-opsz": number
}

// The Ascend mark: two stacked upward chevrons with rounded caps. The top
// chevron is the fixed crimson brand accent; the bottom chevron inherits
// currentColor, so the mark reads crimson + ink on light surfaces and
// crimson + white on dark ones. (Brand set: marketing/brand-assets/.)
function AscendMark({ size, bottomColor }: { size: number; bottomColor: string }) {
  return (
    <svg
      viewBox="0 0 120 120"
      width={size}
      height={size}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      className="shrink-0"
    >
      <path d="M 26 50 L 60 26 L 94 50" stroke="var(--color-vermilion)" strokeWidth="13" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M 26 88 L 60 64 L 94 88" stroke={bottomColor} strokeWidth="13" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

/**
 * Cold Approach wordmark: the Ascend chevron mark next to the Fraunces wordmark.
 * The `mark` variant renders just the stacked-chevron glyph (favicons, app
 * icons, stamps). The `wordmark` variant pairs the glyph with the Fraunces
 * "Cold Approach" lockup: the top chevron stays crimson as a fixed brand accent
 * while the bottom chevron and the wordmark text inherit currentColor (ink), so
 * the lockup reads mostly as ink with crimson confined to the mark.
 */
export function ColdApproachWordmark({ variant = "wordmark", className, size, decorative = false }: WordmarkProps) {
  if (variant === "mark") {
    const markSize = size ?? 28
    return (
      <span
        className={cn("inline-flex text-current", className)}
        aria-label={decorative ? undefined : "Cold Approach"}
        aria-hidden={decorative}
      >
        <AscendMark size={markSize} bottomColor="currentColor" />
      </span>
    )
  }

  const fontSize = size ? `${Math.max(18, size * 0.24)}px` : "2.25rem"

  const opsz = Math.min(96, Math.max(36, (size ?? 108) * 0.85))
  const textStyle: WordmarkTextStyle = {
    "--wordmark-font-size": fontSize,
    "--wordmark-opsz": opsz,
  }
  // Mark scales with the wordmark, using the same vertical rhythm as the lockup.
  const markPx = size ? Math.max(18, size * 0.22) : 24
  return (
    <span
      className={cn("inline-flex items-center gap-2.5 leading-none whitespace-nowrap text-current", className)}
      aria-label={decorative ? undefined : "Cold Approach"}
      aria-hidden={decorative}
    >
      <AscendMark size={markPx} bottomColor="currentColor" />
      <span className="cold-approach-wordmark-text" style={textStyle}>
        Cold Approach
      </span>
    </span>
  )
}
